import { ROLES, type Role } from "@dishour/shared";
import { useState } from "react";
import { useCreateInvite, useRestaurants, useSuppliers } from "../../api/admin.js";
import { AdminShell } from "../../components/AdminShell.js";
import { Field, buttonClass, inputClass } from "../../components/Field.js";

const ROLE_LABELS: Record<Role, string> = {
  admin: "관리자",
  restaurant: "식당",
  supplier: "공급업체",
};

export function AdminInvitesPage() {
  const restaurants = useRestaurants();
  const suppliers = useSuppliers();
  const createInvite = useCreateInvite();
  const [form, setForm] = useState({ email: "", name: "", role: "restaurant" as Role, orgId: "" });
  const [copied, setCopied] = useState(false);

  const orgs = form.role === "restaurant" ? restaurants.data : form.role === "supplier" ? suppliers.data : [];

  return (
    <AdminShell title="초대 관리">
      <form
        className="mb-6 grid max-w-2xl gap-4 rounded-xl border border-stone-200 bg-white p-4 md:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          setCopied(false);
          createInvite.mutate({
            email: form.email,
            name: form.name,
            role: form.role,
            restaurantId: form.role === "restaurant" ? form.orgId : null,
            supplierId: form.role === "supplier" ? form.orgId : null,
          });
        }}
      >
        <Field label="이메일" htmlFor="email">
          <input
            id="email"
            type="email"
            required
            className={inputClass}
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </Field>
        <Field label="이름" htmlFor="name">
          <input
            id="name"
            required
            className={inputClass}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>
        <Field label="역할" htmlFor="role">
          <select
            id="role"
            className={inputClass}
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value as Role, orgId: "" })}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </select>
        </Field>
        {form.role !== "admin" ? (
          <Field label="소속 조직" htmlFor="orgId">
            <select
              id="orgId"
              required
              className={inputClass}
              value={form.orgId}
              onChange={(e) => setForm({ ...form, orgId: e.target.value })}
            >
              <option value="">선택하세요</option>
              {orgs?.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        {createInvite.error ? (
          <p role="alert" className="text-sm text-red-600 md:col-span-2">
            {(createInvite.error as Error).message}
          </p>
        ) : null}
        <button type="submit" className={`${buttonClass} md:col-span-2`} disabled={createInvite.isPending}>
          초대 링크 만들기
        </button>
      </form>

      {createInvite.data ? (
        <div className="max-w-2xl rounded-xl border-2 border-amber-400 bg-amber-50 p-4">
          <p className="mb-2 text-sm font-semibold text-amber-900">
            이 링크는 지금만 볼 수 있습니다. 화면을 벗어나면 다시 확인할 수 없으니, 지금 복사해서 전달하세요.
          </p>
          <code className="block break-all rounded bg-white p-2 text-sm">{createInvite.data.inviteUrl}</code>
          <button
            type="button"
            className="mt-3 w-full rounded-lg bg-brand-700 px-4 py-3 text-base font-semibold text-white hover:bg-brand-800 md:w-auto"
            onClick={async () => {
              await navigator.clipboard.writeText(createInvite.data!.inviteUrl);
              setCopied(true);
            }}
          >
            {copied ? "복사됨 ✓" : "링크 복사하기"}
          </button>
        </div>
      ) : null}
    </AdminShell>
  );
}
